import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Menu, Item, Text, View } from '@adobe/react-spectrum';
import { Selection } from '@react-types/shared';
import styles from './FilterSection.module.scss';
import MenuCheckbox from './MenuCheckbox';

interface EventOption {
  label: string;
  value: string;
  title: string;
  relationships?: any[];
  relationship?: any;
  quarter?: string;
}

interface EventsFilterProps {
  eventOptions: EventOption[];
  selectedEventTitles: Selection;
  onSelectionChange: (selection: Selection) => void;
  onAction?: (key: React.Key) => void;
}

// Search icon SVG component
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.5303 17.4697L13.447 12.3865C14.4164 11.1851 15 9.66003 15 8C15 4.14062 11.8594 1 8 1C4.14062 1 1 4.14061 1 7.99999C1 11.8594 4.14062 15 8 15C9.66003 15 11.1851 14.4164 12.3865 13.447L17.4697 18.5303C17.6162 18.6767 17.8076 18.75 18 18.75C18.1924 18.75 18.3838 18.6768 18.5303 18.5303C18.8232 18.2373 18.8232 17.7627 18.5303 17.4697ZM8 13.5C4.96777 13.5 2.5 11.0322 2.5 7.99999C2.5 4.96776 4.96777 2.49999 8 2.49999C11.0322 2.49999 13.5 4.96776 13.5 7.99999C13.5 11.0322 11.0322 13.5 8 13.5Z"
      fill="#717171"
    />
  </svg>
);

export function EventsFilter({
  eventOptions,
  selectedEventTitles,
  onSelectionChange,
  onAction
}: EventsFilterProps) {
  // Search state
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Count actual events (excluding "All" option)
  const actualEventsCount = useMemo(
    () => eventOptions.filter(event => event.title !== 'All').length,
    [eventOptions]
  );

  // Clear search text when search bar should not be shown
  useEffect(() => {
    if (actualEventsCount <= 12 && searchText) {
      setSearchText('');
    }
  }, [actualEventsCount, searchText]);

  // Filter events based on search text
  const filteredEvents = useMemo(() => {
    if (!searchText.trim()) {
      return eventOptions;
    }

    const searchLower = searchText.toLowerCase();
    return eventOptions.filter(event => {
      // Don't filter out the "All" option
      if (event.title === 'All') {
        return true;
      }

      // Search in title and label
      return (
        event.title.toLowerCase().includes(searchLower) ||
        event.label.toLowerCase().includes(searchLower)
      );
    });
  }, [searchText, eventOptions]);

  // Build the menu items
  const menuItems = useMemo(() => {
    const items: JSX.Element[] = [];

    // Only add search field if there are more than 12 events
    if (actualEventsCount > 12) {
      items.push(
        <Item key="search-field" data-testid="events-search-field-item">
          <Text UNSAFE_className={styles.searchFieldMenuItem}>
            <div className={styles.categorySearchField}>
              <div className={styles.categorySearchIconContainer}>
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.categorySearchInput}
                placeholder="Search events"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                data-testid="events-search-input"
              />
              <button
                className={styles.categoryClearButton}
                onClick={() => {
                  setSearchText('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
                data-testid="events-search-clear"
                data-hidden={!searchText}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                >
                  <path
                    d="M5.23779 4L7.69384 1.54395C8.03565 1.20215 8.03565 0.647469 7.69384 0.305669C7.35204 -0.0361311 6.79834 -0.0361311 6.45654 0.305669L4 2.76221L1.54346 0.305659C1.20166 -0.0361311 0.647955 -0.0361311 0.306155 0.305659C-0.0356453 0.647449 -0.0356353 1.20215 0.306155 1.54395L2.7622 4L0.306155 6.45605C-0.0356453 6.79785 -0.0356453 7.35253 0.306155 7.69433C0.477055 7.86523 0.700685 7.95019 0.924805 7.95019C1.14892 7.95019 1.37255 7.86523 1.54345 7.69433L3.99999 5.23779L6.45653 7.69433C6.62743 7.86523 6.85106 7.95019 7.07518 7.95019C7.2993 7.95019 7.52293 7.86523 7.69383 7.69433C8.03563 7.35253 8.03563 6.79785 7.69383 6.45605L5.23779 4Z"
                    fill="#292929"
                  />
                </svg>
              </button>
            </div>
          </Text>
        </Item>
      );
    }

    // Add filtered event items
    filteredEvents.forEach(item => {
      items.push(
        <Item
          key={item.title}
          data-testid={`filter-section-event-${item.title
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
        >
          <Text UNSAFE_className={styles.menuItemText}>
            <View>
              <MenuCheckbox
                isSelected={
                  selectedEventTitles instanceof Set
                    ? selectedEventTitles.has(item.title)
                    : false
                }
                isDisabled={(item as any).disabled}
              />
            </View>
            <span className={styles.menuItemLabel} title={item.label}>
              {item.label}
            </span>
          </Text>
        </Item>
      );
    });

    // Show "No results" message if search yields no results
    if (filteredEvents.length === 0) {
      items.push(
        <Item key="no-results">
          <Text>No events found</Text>
        </Item>
      );
    }

    return items;
  }, [filteredEvents, selectedEventTitles, actualEventsCount, searchText]);

  // Memoize disabled keys array
  const disabledKeys = useMemo(() => ['no-events', 'no-results'], []);

  // Determine class based on number of filtered events
  const menuClassName = useMemo(() => {
    const baseClass = styles['subMenu'];
    const sizeClass =
      filteredEvents.length < 10
        ? styles['lessThanTen']
        : styles['moreThanTen'];
    return `${baseClass} ${sizeClass}`;
  }, [filteredEvents.length]);

  return (
    <Menu
      data-testid="filter-section-events-menu"
      onSelectionChange={onSelectionChange}
      onAction={onAction}
      UNSAFE_className={menuClassName}
      selectionMode="multiple"
      selectedKeys={selectedEventTitles as any}
      // disabledKeys={disabledKeys}
    >
      {menuItems}
    </Menu>
  );
}

EventsFilter.displayName = 'EventsFilter';
